export * from './normalize';
export * from './reduce';
export * from './types';

import {
  createWorkItemProjectionState,
  projectWorkItemEvents,
} from '@web/lib/remoteWorkItemModel';
import { normalizeEvent } from './normalize';
import {
  compactOwnedProjectView,
  createProjectViewState,
  reduceProjectViewOwned,
  retainRecentRunRecords,
} from './reduce';
import type {
  CanonicalProjectEvent,
  ProjectedArtifact,
  ProjectedHumanInteraction,
  ProjectedLegacyStep,
  ProjectedRun,
  ProjectorMode,
  ProjectSnapshotInput,
  ProjectViewState,
} from './types';

const SNAPSHOT_RUN_STATUSES = new Set<ProjectedRun['status']>([
  'running',
  'completed',
  'failed',
  'cancelled',
  'interrupted',
]);
const CROSS_LANE_MATCH_WINDOW_SECONDS = 120;

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, stableValue(item)])
    );
  }
  return value;
}

function sameData(left: unknown, right: unknown): boolean {
  return (
    JSON.stringify(stableValue(left)) === JSON.stringify(stableValue(right))
  );
}

function mergeSnapshotArtifacts(
  previous: Record<string, ProjectedArtifact[]>,
  incoming: Record<string, ProjectedArtifact[]>
): Record<string, ProjectedArtifact[]> {
  const merged = { ...previous };
  for (const [runId, artifacts] of Object.entries(incoming)) {
    const previousById = new Map(
      (previous[runId] || []).map((artifact) => [artifact.artifactId, artifact])
    );
    merged[runId] = artifacts.map((artifact) => {
      const prior = previousById.get(artifact.artifactId);
      // Upload references are append-only Artifact facts. A snapshot captured
      // immediately before artifact.uploaded may legitimately omit the ref
      // while the subscribe-first live lane has already projected it. Never
      // let that older aggregate erase the more advanced local projection.
      return !artifact.assetRef && prior?.assetRef
        ? { ...artifact, assetRef: prior.assetRef }
        : artifact;
    });
  }
  return merged;
}

function equivalentCrossLaneStep(
  left: ProjectedLegacyStep,
  right: ProjectedLegacyStep
): boolean {
  return (
    left.source !== right.source &&
    left.projectId === right.projectId &&
    left.taskId === right.taskId &&
    left.step === right.step &&
    !left.crossLaneEventIds?.length &&
    !right.crossLaneEventIds?.length &&
    left.timestamp !== null &&
    right.timestamp !== null &&
    Math.abs(left.timestamp - right.timestamp) <=
      CROSS_LANE_MATCH_WINDOW_SECONDS &&
    sameData(left.data, right.data)
  );
}

function mergeSnapshotSteps(
  projected: ProjectedLegacyStep[],
  previous: ProjectedLegacyStep[]
): ProjectedLegacyStep[] {
  const merged = [...previous];
  for (const candidate of projected) {
    const exactIndex = merged.findIndex(
      (existing) =>
        existing.projectId === candidate.projectId &&
        existing.taskId === candidate.taskId &&
        String(existing.stepId) === String(candidate.stepId)
    );
    const equivalentIndex =
      exactIndex >= 0
        ? exactIndex
        : merged.findIndex((existing) =>
            equivalentCrossLaneStep(existing, candidate)
          );
    if (equivalentIndex < 0) {
      merged.push(candidate);
      continue;
    }
    const existing = merged[equivalentIndex];
    merged[equivalentIndex] = {
      ...existing,
      crossLaneEventIds: Array.from(
        new Set([
          ...(existing.crossLaneEventIds || []),
          ...(candidate.crossLaneEventIds || []),
          candidate.eventId,
        ])
      ),
    };
  }
  return merged;
}

function snapshotRunStatus(value: string): ProjectedRun['status'] {
  return SNAPSHOT_RUN_STATUSES.has(value as ProjectedRun['status'])
    ? (value as ProjectedRun['status'])
    : 'running';
}

function mergeCanonicalEvents(
  projected: CanonicalProjectEvent[],
  previous: CanonicalProjectEvent[]
): CanonicalProjectEvent[] {
  const merged = [
    ...previous,
    ...projected.filter(
      (event) =>
        !previous.some((existing) => existing.eventId === event.eventId)
    ),
  ];
  merged.sort((left, right) => {
    if (left.cloudCursor !== null && right.cloudCursor !== null) {
      return left.cloudCursor - right.cloudCursor;
    }
    if (left.runId === right.runId && left.runSequence !== right.runSequence) {
      return left.runSequence - right.runSequence;
    }
    return left.createdAt.localeCompare(right.createdAt);
  });
  return merged.slice(-5000);
}

export function projectRawEvents(
  projectId: string,
  rawEvents: unknown[],
  mode: ProjectorMode,
  initial?: ProjectViewState
): ProjectViewState {
  let state = initial
    ? {
        ...initial,
        workItemProjection: structuredClone(initial.workItemProjection),
      }
    : createProjectViewState(projectId, mode);
  for (const raw of rawEvents) {
    const event =
      raw && typeof raw === 'object' && 'eventId' in raw && 'runSequence' in raw
        ? (raw as CanonicalProjectEvent)
        : normalizeEvent(raw);
    state = reduceProjectViewOwned(state, event);
  }
  return compactOwnedProjectView(state);
}

export function projectSnapshot(
  snapshot: ProjectSnapshotInput,
  previous?: ProjectViewState | null
): ProjectViewState {
  const projected = projectRawEvents(
    snapshot.project_id,
    snapshot.recent_events,
    'rehydrate'
  );
  // Artifact aggregates are intentionally independent of the bounded event
  // tail. Project them as snapshot facts (not cursor deltas) so an old
  // manifest cannot manufacture a gap against the recent tail.
  const artifactProjection = projectRawEvents(
    snapshot.project_id,
    (snapshot.artifact_events || []).map((raw) => ({
      ...normalizeEvent(raw),
      cloudCursor: null,
      source: 'chat_step_v1' as const,
    })),
    'rehydrate'
  );
  const runs = { ...projected.runs };
  for (const aggregate of snapshot.runs || []) {
    const recent = runs[aggregate.run_id];
    const origin = aggregate.origin ?? recent?.origin;
    runs[aggregate.run_id] = {
      ...(origin ? { origin } : {}),
      runId: aggregate.run_id,
      status: snapshotRunStatus(aggregate.status),
      lastSequence: Math.max(
        recent?.lastSequence || 0,
        aggregate.expected_next_run_sequence - 1
      ),
      runVersion: recent?.runVersion || 0,
      updatedAt: aggregate.updated_at,
    };
  }

  const mergePrevious =
    previous !== null &&
    previous !== undefined &&
    previous.projectId === snapshot.project_id;
  if (mergePrevious) {
    for (const [runId, existing] of Object.entries(previous.runs)) {
      const snapshotRun = runs[runId];
      if (
        !snapshotRun ||
        existing.lastSequence > snapshotRun.lastSequence ||
        (existing.lastSequence === snapshotRun.lastSequence &&
          existing.updatedAt > snapshotRun.updatedAt)
      ) {
        runs[runId] = existing;
      }
    }
  }

  const legacySteps = mergePrevious
    ? mergeSnapshotSteps(projected.legacySteps, previous.legacySteps)
    : [...projected.legacySteps];
  const uniqueSteps = legacySteps.filter(
    (step, index, items) =>
      items.findIndex(
        (existing) =>
          existing.projectId === step.projectId &&
          existing.taskId === step.taskId &&
          String(existing.stepId) === String(step.stepId)
      ) === index
  );
  uniqueSteps.sort((left, right) => {
    if (
      left.taskId === right.taskId &&
      left.source === 'canonical' &&
      right.source === 'canonical' &&
      left.runSequence !== right.runSequence
    ) {
      return left.runSequence - right.runSequence;
    }
    if (
      left.timestamp !== null &&
      right.timestamp !== null &&
      left.timestamp !== right.timestamp
    ) {
      return left.timestamp - right.timestamp;
    }
    if (left.cloudCursor !== null && right.cloudCursor !== null) {
      return left.cloudCursor - right.cloudCursor;
    }
    return 0;
  });

  const snapshotCoversResync =
    !mergePrevious ||
    !previous.needsResync ||
    previous.resyncTargetCursor === null ||
    snapshot.current_cursor >= previous.resyncTargetCursor;
  const incomingArtifacts = {
    ...projected.artifactsByRun,
    ...artifactProjection.artifactsByRun,
  };
  const incomingArtifactManifests = {
    ...projected.artifactManifestsByRun,
    ...artifactProjection.artifactManifestsByRun,
  };
  return {
    ...projected,
    seenEventIds: mergePrevious
      ? { ...previous.seenEventIds, ...projected.seenEventIds }
      : projected.seenEventIds,
    currentCursor: Math.max(
      snapshot.current_cursor,
      mergePrevious ? previous.currentCursor : 0
    ),
    eventsTruncated: Boolean(snapshot.events_truncated),
    needsResync: snapshotCoversResync ? false : previous.needsResync,
    resyncReason: snapshotCoversResync ? null : previous.resyncReason,
    resyncTargetCursor: snapshotCoversResync
      ? null
      : previous.resyncTargetCursor,
    runs,
    artifactsByRun: mergePrevious
      ? mergeSnapshotArtifacts(previous.artifactsByRun || {}, incomingArtifacts)
      : incomingArtifacts,
    artifactManifestsByRun: retainRecentRunRecords(
      mergePrevious
        ? {
            ...(previous.artifactManifestsByRun || {}),
            ...incomingArtifactManifests,
          }
        : incomingArtifactManifests,
      Object.keys(incomingArtifactManifests).at(-1) ||
        Object.keys(previous?.artifactManifestsByRun || {}).at(-1) ||
        ''
    ),
    legacySteps: uniqueSteps,
    canonicalEvents: mergePrevious
      ? mergeCanonicalEvents(
          projected.canonicalEvents,
          previous.canonicalEvents || []
        )
      : projected.canonicalEvents,
    workItemProjection: mergePrevious
      ? projectWorkItemEvents(
          projected.canonicalEvents,
          previous.workItemProjection || createWorkItemProjectionState()
        )
      : projected.workItemProjection,
    unknownEvents: mergePrevious
      ? [
          ...projected.unknownEvents,
          ...previous.unknownEvents.filter(
            (event) =>
              !projected.unknownEvents.some(
                (existing) => existing.eventId === event.eventId
              )
          ),
        ]
      : projected.unknownEvents,
  };
}

export function selectRunArtifacts(
  view: ProjectViewState,
  runId: string
): ProjectedArtifact[] {
  return view.artifactsByRun[runId] || [];
}

const CLOSED_ASK_STEPS = new Set(['end', 'human_reply']);

export function selectPendingLegacyAsk(
  view: ProjectViewState,
  answeredStepIds: ReadonlySet<number | string>
): ProjectedLegacyStep | null {
  const closedRuns = new Set<string>();
  for (let index = view.legacySteps.length - 1; index >= 0; index -= 1) {
    const step = view.legacySteps[index];
    const run = view.runs[step.taskId];
    if (run && run.status !== 'running') {
      closedRuns.add(step.taskId);
      continue;
    }
    if (CLOSED_ASK_STEPS.has(step.step)) {
      closedRuns.add(step.taskId);
      continue;
    }
    if (step.step !== 'ask') continue;
    if (answeredStepIds.has(step.stepId)) {
      closedRuns.add(step.taskId);
      continue;
    }
    if (!closedRuns.has(step.taskId)) return step;
  }
  return null;
}

const EXPIRED_INTERACTION_HISTORY_MS = 24 * 60 * 60 * 1000;
const sortedUnknownEventCache = new WeakMap<
  ProjectViewState,
  CanonicalProjectEvent[]
>();
const humanInteractionIndexCache = new WeakMap<
  ProjectViewState,
  {
    pending: ProjectedHumanInteraction[];
    expired: ProjectedHumanInteraction[];
  }
>();

function sortedUnknownEvents(view: ProjectViewState): CanonicalProjectEvent[] {
  const cached = sortedUnknownEventCache.get(view);
  if (cached) return cached;
  const events = [...view.unknownEvents].sort((left, right) => {
    if (left.cloudCursor !== null && right.cloudCursor !== null) {
      return left.cloudCursor - right.cloudCursor;
    }
    return left.createdAt.localeCompare(right.createdAt);
  });
  sortedUnknownEventCache.set(view, events);
  return events;
}

function interactionType(
  payload: Record<string, unknown>,
  approval: boolean
): ProjectedHumanInteraction['interactionType'] {
  if (approval) return 'approval';
  const value = payload.interaction_type;
  return value === 'question' ||
    value === 'choice' ||
    value === 'form' ||
    value === 'confirmation' ||
    value === 'approval' ||
    value === 'diff_review' ||
    value === 'merge_conflict' ||
    value === 'credential_binding' ||
    value === 'memory_change_review'
    ? value
    : 'unknown';
}

function interactionExpiry(payload: Record<string, unknown>): string | null {
  const value = payload.expires_at;
  if (typeof value === 'string' && Number.isFinite(Date.parse(value))) {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(
      value > 10_000_000_000 ? value : value * 1000
    ).toISOString();
  }
  return null;
}

function projectHumanInteractions(view: ProjectViewState): {
  pending: ProjectedHumanInteraction[];
  expired: ProjectedHumanInteraction[];
} {
  const cached = humanInteractionIndexCache.get(view);
  if (cached) return cached;
  const pending = new Map<string, ProjectedHumanInteraction>();
  const expired: ProjectedHumanInteraction[] = [];
  for (const event of sortedUnknownEvents(view)) {
    const payload = event.payload;
    const interactionId = String(
      payload.interaction_id || payload.approval_id || ''
    );
    if (!interactionId) continue;
    if (
      event.eventType === 'interaction.expired' ||
      event.eventType === 'approval.expired' ||
      event.eventType === 'approval.expired_rejected'
    ) {
      const request = pending.get(interactionId);
      if (request) {
        expired.push({
          ...request,
          status: 'expired',
          createdAt: event.createdAt,
        });
      }
      pending.delete(interactionId);
      continue;
    }
    if (
      event.eventType === 'interaction.resolved' ||
      event.eventType === 'approval.decided'
    ) {
      pending.delete(interactionId);
      continue;
    }
    if (
      event.eventType !== 'interaction.requested' &&
      event.eventType !== 'approval.requested'
    ) {
      continue;
    }
    const approval = event.eventType === 'approval.requested';
    const resolvedInteractionType = interactionType(payload, approval);
    const request = (approval ? payload.prompt : payload.request) as
      | Record<string, unknown>
      | undefined;
    pending.set(interactionId, {
      interactionId,
      interactionType: resolvedInteractionType,
      runId: event.runId,
      version:
        typeof payload.version === 'number' && Number.isInteger(payload.version)
          ? payload.version
          : 0,
      request: request || {},
      options: Array.isArray(payload.options)
        ? (payload.options as Array<Record<string, unknown>>)
        : [],
      actionDigest:
        typeof payload.action_digest === 'string'
          ? payload.action_digest
          : null,
      createdAt: event.createdAt,
      expiresAt: interactionExpiry(payload),
      status: 'pending',
    });
  }
  const indexed = { pending: [...pending.values()], expired };
  humanInteractionIndexCache.set(view, indexed);
  return indexed;
}

export function selectPendingHumanInteraction(
  view: ProjectViewState,
  locallyAnsweredIds: ReadonlySet<string> = new Set(),
  now = Date.now()
): ProjectedHumanInteraction | null {
  return (
    projectHumanInteractions(view)
      .pending.filter((item) => !locallyAnsweredIds.has(item.interactionId))
      .filter(
        (item) => item.expiresAt === null || Date.parse(item.expiresAt) > now
      )
      .sort((left, right) =>
        right.createdAt.localeCompare(left.createdAt)
      )[0] || null
  );
}

export function selectLatestExpiredHumanInteraction(
  view: ProjectViewState,
  now = Date.now()
): ProjectedHumanInteraction | null {
  const indexed = projectHumanInteractions(view);
  const expired = [...indexed.expired];
  for (const request of indexed.pending) {
    if (request.expiresAt && Date.parse(request.expiresAt) <= now) {
      expired.push({
        ...request,
        status: 'expired',
        createdAt: request.expiresAt,
      });
    }
  }
  return (
    expired
      .filter(
        (item) =>
          now - Date.parse(item.createdAt) <= EXPIRED_INTERACTION_HISTORY_MS
      )
      .sort((left, right) =>
        right.createdAt.localeCompare(left.createdAt)
      )[0] || null
  );
}
